import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';

import { SEARCH_MAX_LENGTH } from '@/components/ui/FilterBar';

/**
 * The list's view, kept in the page's address (P8): the search, the Status
 * and ARC Placement filters and the page. A reload and the back button
 * return to the same view. Defaults are left out of the address, and a value
 * outside its list falls back to the default rather than reaching the
 * backend's 422. There is no range: the list takes the backend's 30 days
 * (P9).
 */

export const STATUSES = ['DRAFT', 'PUBLISHED', 'INACTIVE', 'ARCHIVED'] as const;
export type Status = (typeof STATUSES)[number] | '';

export const PLACEMENTS = ['in', 'not_in'] as const;
export type Placement = (typeof PLACEMENTS)[number] | '';

export interface PagesParams {
  search: string;
  /** A lifecycle, or empty for any but Removed (backend D23). */
  status: Status;
  placement: Placement;
  page: number;
}

export const DEFAULTS: PagesParams = {
  search: '',
  status: '',
  placement: '',
  page: 1,
};

function oneOf<T extends string>(value: string | null, allowed: readonly T[]): T | '' {
  return allowed.find((candidate) => candidate === value) ?? '';
}

export function parseParams(search: URLSearchParams): PagesParams {
  const page = Number(search.get('page'));
  return {
    // Cut to the search box's own limit, the backend's 200 (a longer one is a 422).
    search: (search.get('search') ?? DEFAULTS.search).slice(0, SEARCH_MAX_LENGTH),
    status: oneOf(search.get('status'), STATUSES),
    placement: oneOf(search.get('placement'), PLACEMENTS),
    page: Number.isInteger(page) && page >= 1 ? page : DEFAULTS.page,
  };
}

export function serializeParams(params: PagesParams): URLSearchParams {
  const search = new URLSearchParams();
  for (const key of Object.keys(DEFAULTS) as (keyof PagesParams)[]) {
    if (params[key] !== DEFAULTS[key]) search.set(key, String(params[key]));
  }
  return search;
}

/**
 * A new view from a change. Anything but turning the page returns to the
 * first page, because the old page number belongs to the old list.
 */
export function withChange(
  params: PagesParams,
  patch: Partial<PagesParams>,
): PagesParams {
  const pageOnly = Object.keys(patch).every((key) => key === 'page');
  return { ...params, ...(pageOnly ? {} : { page: 1 }), ...patch };
}

export function usePagesParams(): [PagesParams, (patch: Partial<PagesParams>) => void] {
  const [searchParams, setSearchParams] = useSearchParams();
  const params = useMemo(() => parseParams(searchParams), [searchParams]);
  const change = useCallback(
    (patch: Partial<PagesParams>) => {
      setSearchParams(serializeParams(withChange(parseParams(searchParams), patch)), {
        replace: true,
      });
    },
    [searchParams, setSearchParams],
  );
  return [params, change];
}
